/**
 * ────────────────────────────────────────────────────────────────────────────
 * PUSH NOTIFICATIONS — APNs (iOS) / FCM (Android) vía `@capacitor/push-notifications`
 * ────────────────────────────────────────────────────────────────────────────
 * Avisos de reserva aceptada/rechazada, mensajes de chat, SOS/incidentes. SÓLO
 * tiene sentido en el shell nativo (`isNativePlatform()`): en web devuelve
 * `{status:"unavailable"}` sin pedir nada — Web Push (Notification API +
 * Service Worker) queda FUERA de este lote (la PWA hoy no registra un SW para
 * push; si se agrega en el futuro, esta misma interfaz (`registerForPush`) es
 * el lugar para sumar la rama web).
 *
 * DEGRADACIÓN: si el usuario niega el permiso nativo, o el registro con
 * APNs/FCM falla (red, no hay `google-services.json`/certificado APNs aún —
 * ver Agente B), la función resuelve un status claro y NUNCA lanza. La app
 * sigue funcionando sin push; sólo se pierde el aviso proactivo.
 *
 * QUIÉN LA LLAMA: `NativeBootstrapClient` (montado una vez en el layout raíz,
 * ver abajo) dispara el registro apenas la WebView carga, sin esperar a que el
 * usuario abra ninguna pantalla en particular. Si más adelante se quiere pedir
 * el permiso en un momento más "con contexto" (p.ej. tras la primera reserva),
 * basta con mover la llamada a `registerForPushNotifications` a ese punto.
 */
import { isNativePlatform, nativePlatform } from "./capabilities";

export interface PushToken {
  token: string;
  platform: "ios" | "android";
}

export type PushRegistrationStatus =
  | "registered"
  | "denied"
  | "unavailable"
  | "error";

export interface PushRegistrationResult {
  status: PushRegistrationStatus;
  token?: string;
}

/**
 * Pide permiso (si hace falta) y registra el device token. En web resuelve
 * `{status:"unavailable"}` de inmediato. Nunca lanza.
 */
export async function registerForPushNotifications(): Promise<PushRegistrationResult> {
  if (!isNativePlatform()) {
    return { status: "unavailable" };
  }

  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") {
      return { status: "denied" };
    }

    return await new Promise((resolve) => {
      // `addListener` es fire-and-forget del lado de Capacitor; no hace falta
      // guardar el handle para este uso (registro único al boot de la app).
      PushNotifications.addListener("registration", (token) => {
        resolve({ status: "registered", token: token.value });
      });
      PushNotifications.addListener("registrationError", () => {
        resolve({ status: "error" });
      });
      PushNotifications.register();
    });
  } catch {
    return { status: "error" };
  }
}

/**
 * Envía el token al server (endpoint stub — ver `src/app/api/native/push-token/route.ts`).
 * Devuelve `false` sin lanzar si falla la red o el server rechaza el body
 * (nunca debe tumbar el arranque de la app).
 */
export async function sendPushTokenToServer(token: string): Promise<boolean> {
  try {
    const res = await fetch("/api/native/push-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, platform: nativePlatform() }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Atajo: registra + envía al server en un solo paso. Es lo que llama
 * `NativeBootstrapClient`. No hace nada en web (`unavailable`, no-op).
 */
export async function bootstrapPushNotifications(): Promise<PushRegistrationResult> {
  const result = await registerForPushNotifications();
  if (result.status === "registered" && result.token) {
    await sendPushTokenToServer(result.token);
  }
  return result;
}
